import type { Reminder } from '../../lib/remindersApi';
import type { VideoMeetingSummary } from '../../lib/meetApi';
import type { CalendarEvent } from '../../lib/calendarApi';
import { formatEstimate as formatMinutes } from '../../lib/reminderDates';
import { PreviewRow } from '../tasks/TaskHoverCard';
import { useI18n } from '../../contexts/I18nContext';

/**
 * Hover previews for the two non-task chips on the calendar, in the shape of
 * the task preview: a breadcrumb line, the title, an optional teaser, then
 * "label: value" rows. Hovering a meeting or a reminder reads the same as
 * hovering a task.
 */

/** Sky for meetings, violet for reminders — the chip tints, see chips.tsx. */
export const MEETING_COLOR = '#0ea5e9';
// Red (Andrei, 2026-09-29): a reminder is a deadline, the same red as the
// strip at the top of every page; violet read as "just another status".
export const REMINDER_COLOR = '#ef4444';
const CANCELLED_COLOR = '#6b7280';
// An event from the person's Google Calendar wears the colour Google paints it
// with there — the event's own, else its calendar's — so the two screens match
// (Andrei, 2026-09-30). Green is only the fallback when that is unknown.
export const CALENDAR_EVENT_COLOR = '#16a34a';

export function calendarEventColor(event: CalendarEvent): string {
  return event.color ?? CALENDAR_EVENT_COLOR;
}

/** Minutes between two instants, for the chip's duration. */
export function calendarEventMinutes(event: CalendarEvent): number {
  return Math.max(
    0,
    Math.round((new Date(event.endTime).getTime() - new Date(event.startTime).getTime()) / 60_000),
  );
}

export function meetingStatusLabel(
  status: VideoMeetingSummary['status'],
  t: (key: string) => string,
): string {
  return t(`meet.status.${status.toLowerCase()}`);
}

export function meetingColor(meeting: VideoMeetingSummary): string {
  return meeting.status === 'CANCELLED' ? CANCELLED_COLOR : MEETING_COLOR;
}

function whenLabel(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

export function MeetingPreviewCard({ meeting }: { meeting: VideoMeetingSummary }) {
  const { t, locale } = useI18n();
  const color = meetingColor(meeting);
  return (
    <div className="p-3.5">
      <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500">
        <span className="shrink-0">{t('meet.title')}</span>
        {meeting.projectName && (
          <>
            <span className="shrink-0">›</span>
            <span className="min-w-0 truncate">{meeting.projectName}</span>
          </>
        )}
      </div>
      <p className="mt-1 line-clamp-2 text-[14px] font-semibold leading-snug text-gray-900">
        {meeting.title}
      </p>
      <div className="mt-3 space-y-1.5 border-t border-gray-200 pt-3">
        <PreviewRow label={t('taskPreview.status')}>
          <span
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="truncate" style={{ color }}>
            {meetingStatusLabel(meeting.status, t)}
          </span>
        </PreviewRow>
        <PreviewRow label={t('meetPreview.when')}>
          <span className="truncate">{whenLabel(meeting.scheduledAt, locale)}</span>
        </PreviewRow>
        <PreviewRow label={t('meetPreview.duration')}>
          <span className="truncate">{formatMinutes(meeting.durationMinutes)}</span>
        </PreviewRow>
        <PreviewRow label={t('meetPreview.host')}>
          <span className="truncate">{meeting.hostName}</span>
        </PreviewRow>
        {meeting.clientName && (
          <PreviewRow label={t('meetPreview.client')}>
            <span className="truncate">{meeting.clientName}</span>
          </PreviewRow>
        )}
        {meeting.participantCount > 0 && (
          <PreviewRow label={t('meetPreview.participants')}>
            <span className="truncate">{meeting.participantCount}</span>
          </PreviewRow>
        )}
      </div>
    </div>
  );
}

export function CalendarEventPreviewCard({ event }: { event: CalendarEvent }) {
  const { t, locale } = useI18n();
  const dayFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
  return (
    <div className="p-3.5">
      <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500">
        <span className="shrink-0">{t('calendar.googleEvent')}</span>
        <span className="shrink-0">›</span>
        <span className="min-w-0 truncate">{event.calendarName}</span>
      </div>
      <p className="mt-1 line-clamp-2 text-[14px] font-semibold leading-snug text-gray-900">
        {event.summary}
      </p>
      {event.description && (
        <p className="mt-1.5 line-clamp-3 whitespace-pre-line text-[12.5px] leading-relaxed text-gray-500">
          {event.description}
        </p>
      )}
      <div className="mt-3 space-y-1.5 border-t border-gray-200 pt-3">
        <PreviewRow label={t('meetPreview.when')}>
          <span className="truncate">
            {event.allDay
              ? `${dayFmt.format(new Date(event.startTime))} · ${t('calendar.allDay')}`
              : whenLabel(event.startTime, locale)}
          </span>
        </PreviewRow>
        {!event.allDay && (
          <PreviewRow label={t('meetPreview.duration')}>
            <span className="truncate">{formatMinutes(calendarEventMinutes(event))}</span>
          </PreviewRow>
        )}
        {event.location && (
          <PreviewRow label={t('calendarPreview.location')}>
            <span className="truncate">{event.location}</span>
          </PreviewRow>
        )}
      </div>
    </div>
  );
}

export function ReminderPreviewCard({ reminder }: { reminder: Reminder }) {
  const { t, locale } = useI18n();
  return (
    <div className="p-3.5">
      <div className="text-[11.5px] text-gray-500">{t('reminders.title')}</div>
      <p className="mt-1 line-clamp-2 text-[14px] font-semibold leading-snug text-gray-900">
        {reminder.title}
      </p>
      {reminder.description && (
        <p className="mt-1.5 line-clamp-3 text-[12.5px] leading-relaxed text-gray-500">
          {reminder.description}
        </p>
      )}
      <div className="mt-3 space-y-1.5 border-t border-gray-200 pt-3">
        <PreviewRow label={t('meetPreview.when')}>
          <span className="truncate">{whenLabel(reminder.dueAt, locale)}</span>
        </PreviewRow>
        {reminder.estimatedMinutes != null && (
          <PreviewRow label={t('taskPreview.estimate')}>
            <span className="truncate">{formatMinutes(reminder.estimatedMinutes)}</span>
          </PreviewRow>
        )}
      </div>
    </div>
  );
}
